import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import HoverCard from '@ui/cards/hover-card/hover-card';
import HoverContent from '@ui/cards/hover-card/hover-content';
import PreHoverContent from '@ui/cards/hover-card/pre-hover-content';
import { Session } from '@/types/session.types';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import extractTextFromHtmlString from '@/utils/extract-text-from-html-string';
import Button from '@ui/buttons/button/button';
import { useAppSelector } from '@/store/hooks';
import { getPublishedStatus } from '@/store/slices/project/selectors';
import { getBaseAdminURL } from '@/utils/urls';
import { getDateWithCache } from '@/utils/dates';
import OptionalComponent from '@ui/optional-component/optional-component';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { EProjectStatus } from '@/types/projects.types';
import { EPermissions } from '@/types/permissions.types';
import AuthWrapper from '@ui/auth-wrapper/auth-wrapper';
import { getDefaultAgendaImage } from '@/utils/modules/get-default-agenda-image';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import ProjectColorLink from '@ui/links/project-color-link';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

type SessionCardProps = {
  session: Session;
  template: string;
  index: number;
};

const SessionCard = ({ session, template, index }: SessionCardProps) => {
  const publishedStatus = useAppSelector(getPublishedStatus);
  const isPublished = publishedStatus?.status === EProjectStatus.PUBLIC;

  const location = useLocation();
  const params = useParams();

  const projectUuid = params?.projectUuid;
  const pathname = location.pathname;
  const language = params?.language;

  const handleNavigateToModerator = (session: Session) => {
    if (!projectUuid || !isPublished) return;
    const language = session?.default_language || 'en';
    const url = `${getBaseAdminURL()}/moderator/event/${projectUuid}/${language}/session/${session.uuid}`;
    window.open(url, '_self');
  };

  const startString = useMemo(() => {
    let _startDateString = '';
    let _startString = '';
    if (session.timestamp) {
      const startDate = getDateWithCache(session.timestamp);
      _startDateString = startDate.toLocaleDateString(navigator.language, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
      _startString = _startDateString.replace('at', '•');
    }
    return _startString;
  }, [session.timestamp]);

  const title = language ? session.title?.[language] || session.title.base : session.title.base;
  const description = (
    language ? session.description?.[language] || session?.description?.base || '' : session?.description?.base || ''
  ) as string;

  return (
    <HoverCard height="180px" width="320px" delay={100} backgroundColor="var(--gray-900)">
      <PreHoverContent
        backgroundColor="var(--gray-900)"
        backgroundImage={session.image ?? getDefaultAgendaImage(template, index)}
      >
        <Container
          height="100%"
          width="100%"
          className="padding-16 flex flex-column jc-flex-end"
          borderRadius="var(--card-radius)"
          gradientOverlay={
            session.image
              ? {
                  height: '100%',
                  background: 'linear-gradient(to top, rgba(var(--gray-1000-rgb), 0.8), rgba(var(--gray-1000-rgb), 0))',
                }
              : undefined
          }
        >
          <div className="z-1">
            <OptionalComponent display={!!session.timestamp}>
              <div className="S3 margin-b-4 ">{startString}</div>
            </OptionalComponent>
            <Text tag="h5" clamp={6} className="H5 ">
              {title}
            </Text>
          </div>
        </Container>
      </PreHoverContent>

      <HoverContent backgroundColor="var(--gray-900)">
        <Container
          background="var(--gray-900)"
          className="flex flex-column jc-flex-start padding-16"
          width="100%"
          height="100%"
          borderRadius="var(--card-radius)"
        >
          <div className="flex-1">
            <OptionalComponent display={!!session.timestamp}>
              <Text className="S3 margin-b-4">{startString}</Text>
            </OptionalComponent>
            <Text tag="h5" clamp={1} className="H5 margin-b-8">
              {title}
            </Text>
            <Text tag="div" clamp={2} whiteSpace="pre-wrap" className="S3">
              {extractTextFromHtmlString(description)}
            </Text>
            {/*
							Keeping around as a reference in case product change their mind about
							switching session descriptions back to quill/html
						*/}
            {/* <Text tag="div" clamp={3} whiteSpace="normal" className="H5">
							<RenderQuillContent description={session.description?.base || ''} />
						</Text> */}
          </div>
          <div className="flex fd-row jc-flex-start ai-center gap-4">
            <ProjectColorLink
              textColor="none"
              borderRadius="100px"
              width="65px"
              height="40px"
              to={`${pathname}/session/${session.uuid}`}
              className="flex fd-row jc-center ai-center"
            >
              <Text
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                className="L1"
              >
                Details
              </Text>
            </ProjectColorLink>
            <AuthWrapper allowedRoles={[EPermissions.GroupAdmin, EPermissions.Moderation]}>
              <TooltipPortal
                tooltipContent={
                  <Text color="var(--gray-true-dark-100)" className="C2">
                    Project not yet published
                  </Text>
                }
                disabled={isPublished}
                showOnFocus
                delayStart={300}
                animationType="slide-up"
                keepOpenOnContentHover={false}
                verticalOffset={-2}
              >
                <Button
                  width="104px"
                  onClick={() => handleNavigateToModerator(session)}
                  disabled={!isPublished}
                  design={TButtonAndLinkDesigns.LocalBttnSec}
                  designSize="medium"
                >
                  <Text>Moderate</Text>
                </Button>
              </TooltipPortal>
            </AuthWrapper>
          </div>
        </Container>
      </HoverContent>
    </HoverCard>
  );
};

export default SessionCard;
